import { TeamSelectButton } from './TeamSelectButton'
import { getDisplayName, getTeamLogo, formatOdds, isSameTeam } from '@/lib/teams'
import type { MatchdayFixture } from '../types'

interface FixtureCardProps {
  fixture: MatchdayFixture
  teams?: { name: string; logo: string }[]
  homeForm?: string
  awayForm?: string
  homeRank?: number
  awayRank?: number
  homeUsage: number
  awayUsage: number
  selectedTeam: string | null
  tddTeam: string | null
  tddRemaining: number
  homeTddEligible: boolean
  awayTddEligible: boolean
  onSelectTeam?: (teamName: string) => void
  onDeselectTeam?: () => void
  onToggleTdd?: (enabled: boolean) => void
  onViewTeamInfo?: (teamName: string) => void
}

function formatMatchTime(kickoff: string): { day: string; time: string } {
  const date = new Date(kickoff)
  const day = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return { day, time }
}

export function FixtureCard({
  fixture,
  teams,
  homeForm,
  awayForm,
  homeRank,
  awayRank,
  homeUsage,
  awayUsage,
  selectedTeam,
  tddTeam,
  tddRemaining,
  homeTddEligible,
  awayTddEligible,
  onSelectTeam,
  onDeselectTeam,
  onToggleTdd,
  onViewTeamInfo,
}: FixtureCardProps) {
  const { day, time } = formatMatchTime(fixture.kickoff_time)
  const hasSelection = selectedTeam !== null
  const homeDisplay = getDisplayName(fixture.home_team)
  const awayDisplay = getDisplayName(fixture.away_team)
  // Use isSameTeam to handle formal vs informal name mismatches
  // (e.g., selectedTeam="Tottenham Hotspur" vs fixture.home_team="Tottenham")
  const isHomeSelected = selectedTeam !== null && isSameTeam(selectedTeam, fixture.home_team)
  const isAwaySelected = selectedTeam !== null && isSameTeam(selectedTeam, fixture.away_team)

  const homeLogo = getTeamLogo(fixture.home_team, teams)
  const awayLogo = getTeamLogo(fixture.away_team, teams)

  return (
    <div className="bg-white dark:bg-zinc-900 rounded-2xl border border-zinc-200 dark:border-zinc-800 overflow-hidden">
      {/* Match Header */}
      <div className="px-4 py-3 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <span>{day}</span>
            <span className="w-1 h-1 rounded-full bg-zinc-300 dark:bg-zinc-600" />
            <span className="font-medium text-zinc-900 dark:text-zinc-100">{time}</span>
          </div>
          {fixture.venue && (
            <span className="text-xs text-zinc-500 dark:text-zinc-500 truncate max-w-[140px]" title={fixture.venue}>
              {fixture.venue}
            </span>
          )}
        </div>
      </div>

      {/* Mobile: Row Layout - Compact */}
      <div className="sm:hidden p-3 space-y-2">
        <TeamSelectButton
          variant="row"
          teamName={fixture.home_team}
          teamLogo={homeLogo}
          form={homeForm}
          rank={homeRank}
          odds={fixture.home_win_odds}
          usage={homeUsage}
          isSelected={isHomeSelected}
          isDisabled={hasSelection && !isHomeSelected}
          tddEnabled={tddTeam !== null && isSameTeam(tddTeam, fixture.home_team)}
          tddEligible={homeTddEligible}
          canUseTdd={tddRemaining > 0}
          onSelect={() => onSelectTeam?.(fixture.home_team)}
          onDeselect={onDeselectTeam}
          onToggleTdd={onToggleTdd}
          onViewInfo={() => onViewTeamInfo?.(fixture.home_team)}
        />

        <div className="flex items-center gap-2 px-2">
          <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
          <div className="flex items-center gap-1 px-2 py-0.5 rounded bg-zinc-50 dark:bg-zinc-800/50">
            <span className="text-[10px] text-zinc-400 dark:text-zinc-500">Draw</span>
            {fixture.draw_odds != null && (
              <span className="text-xs font-mono font-bold text-zinc-600 dark:text-zinc-400">
                {formatOdds(fixture.draw_odds)}
              </span>
            )}
          </div>
          <div className="flex-1 h-px bg-zinc-200 dark:bg-zinc-700" />
        </div>

        <TeamSelectButton
          variant="row"
          teamName={fixture.away_team}
          teamLogo={awayLogo}
          form={awayForm}
          rank={awayRank}
          odds={fixture.away_win_odds}
          usage={awayUsage}
          isSelected={isAwaySelected}
          isDisabled={hasSelection && !isAwaySelected}
          tddEnabled={tddTeam !== null && isSameTeam(tddTeam, fixture.away_team)}
          tddEligible={awayTddEligible}
          canUseTdd={tddRemaining > 0}
          onSelect={() => onSelectTeam?.(fixture.away_team)}
          onDeselect={onDeselectTeam}
          onToggleTdd={onToggleTdd}
          onViewInfo={() => onViewTeamInfo?.(fixture.away_team)}
        />
      </div>

      {/* Tablet+: Column Layout */}
      <div className="hidden sm:block p-3">
        <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-2 items-stretch">
          <TeamSelectButton
            variant="column"
            teamName={fixture.home_team}
            teamLogo={homeLogo}
            form={homeForm}
            rank={homeRank}
            odds={fixture.home_win_odds}
            usage={homeUsage}
            isSelected={isHomeSelected}
            isDisabled={hasSelection && !isHomeSelected}
            tddEnabled={tddTeam !== null && isSameTeam(tddTeam, fixture.home_team)}
            tddEligible={homeTddEligible}
            canUseTdd={tddRemaining > 0}
            onSelect={() => onSelectTeam?.(fixture.home_team)}
            onDeselect={onDeselectTeam}
            onToggleTdd={onToggleTdd}
            onViewInfo={() => onViewTeamInfo?.(fixture.home_team)}
          />

          <div className="flex flex-col items-center justify-center gap-2 px-1">
            <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500">VS</span>
            <div className="px-2 py-1.5 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 text-center">
              <p className="text-[10px] text-zinc-400 dark:text-zinc-500">Draw</p>
              {fixture.draw_odds != null && (
                <p className="text-xs font-mono font-bold text-zinc-600 dark:text-zinc-400">
                  {formatOdds(fixture.draw_odds)}
                </p>
              )}
            </div>
          </div>

          <TeamSelectButton
            variant="column"
            teamName={fixture.away_team}
            teamLogo={awayLogo}
            form={awayForm}
            rank={awayRank}
            odds={fixture.away_win_odds}
            usage={awayUsage}
            isSelected={isAwaySelected}
            isDisabled={hasSelection && !isAwaySelected}
            tddEnabled={tddTeam !== null && isSameTeam(tddTeam, fixture.away_team)}
            tddEligible={awayTddEligible}
            canUseTdd={tddRemaining > 0}
            onSelect={() => onSelectTeam?.(fixture.away_team)}
            onDeselect={onDeselectTeam}
            onToggleTdd={onToggleTdd}
            onViewInfo={() => onViewTeamInfo?.(fixture.away_team)}
          />
        </div>
      </div>
    </div>
  )
}
